/**
 * Image Text USP Carousel
 * Initializes Splide carousel and custom progress bar for image-text-usp sections.
 */

(function() {

  const initializedSections = new Set();

  /**
   * Initialize image text USP carousel for a section.
   * @param {string} sectionId - Section ID
   * @param {string} splideId - Splide element ID
   */
  window.initImageTextUsp = function(sectionId, splideId) {
    if (initializedSections.has(sectionId)) {
      return;
    }

    // Wait for DOM if needed
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        window.initImageTextUsp(sectionId, splideId);
      });
      return;
    }

    const sectionContainer = document.getElementById('shopify-section-' + sectionId);
    if (!sectionContainer) {
      return;
    }

    const splideElement = document.getElementById(splideId);
    if (!splideElement) {
      return;
    }

    // Wait for Splide library
    const initCarousel = async function() {
      if (typeof window.__splideReady !== 'undefined') {
        await window.__splideReady;
      } else {
        await new Promise(function(resolve) {
          if (window.Splide) {
            resolve();
          } else {
            const checkSplide = setInterval(function() {
              if (window.Splide) {
                clearInterval(checkSplide);
                resolve();
              }
            }, 100);
          }
        });
      }

      if (!window.Splide) {
        return;
      }

      // Get config from data attribute
      const configAttr = splideElement.getAttribute('data-splide-config');
      if (!configAttr) {
        console.warn('Splide config not found in data-splide-config attribute');
        return;
      }

      let config;
      try {
        config = JSON.parse(configAttr);
      } catch (e) {
        console.warn('Invalid Splide config:', e);
        return;
      }

      const splide = new window.Splide('#' + splideId, config);
      splide.mount();

      // Initialize progress bar
      const progressBar = sectionContainer.querySelector('[data-progress-track]');
      const progressThumb = sectionContainer.querySelector('[data-progress-thumb]');
      const slides = sectionContainer.querySelectorAll('.splide__slide');

      if (progressBar && progressThumb && slides.length > 1) {
        const progressBarController = new ProgressBarController(
          progressBar,
          progressThumb,
          splide,
          slides.length
        );
        progressBarController.init();
      }

      // Initialize video players (uses existing video-player.js)
      if (typeof window.initVideoPlayers === 'function') {
        window.initVideoPlayers(sectionContainer);
      }

      initializedSections.add(sectionId);
    };

    initCarousel();
  };

  /**
   * Progress Bar Controller
   * Handles custom progress bar interactions for carousel navigation.
   */
  class ProgressBarController {
    constructor(track, thumb, splideInstance, totalSlides) {
      this.track = track;
      this.thumb = thumb;
      this.splide = splideInstance;
      this.totalSlides = totalSlides;
      this.currentSlideIndex = 0;
      this.isDragging = false;
      this.dragPointerId = null;
      this.dragInitialClientX = 0;
      this.dragInitialOffset = 0;
      this.dragOffsetPercentage = null;

      // Bind methods
      this.handleTrackClick = this.handleTrackClick.bind(this);
      this.handleWheelScroll = this.handleWheelScroll.bind(this);
      this.startHandleDrag = this.startHandleDrag.bind(this);
      this.handleThumbDragMove = this.handleThumbDragMove.bind(this);
      this.stopHandleDrag = this.stopHandleDrag.bind(this);
      this.handleThumbKeydown = this.handleThumbKeydown.bind(this);
      this.updateProgressBar = this.updateProgressBar.bind(this);
    }

    /**
     * Initialize progress bar event listeners.
     */
    init() {
      this.updateProgressBar();

      this.splide.on('mounted', this.updateProgressBar);
      this.splide.on('moved', this.updateProgressBar);

      this.track.addEventListener('click', this.handleTrackClick);
      this.track.addEventListener('wheel', this.handleWheelScroll, { passive: false });
      this.thumb.addEventListener('pointerdown', this.startHandleDrag);
      this.thumb.addEventListener('keydown', this.handleThumbKeydown);

      // Add wheel scroll to carousel container
      const carouselContainer = this.track.closest('.image-text-usp__items');
      if (carouselContainer) {
        carouselContainer.addEventListener('wheel', this.handleWheelScroll, { passive: false });
      }
    }

    /**
     * Calculate handle width percentage.
     * @returns {number}
     */
    getHandleWidthPercentage() {
      if (!this.totalSlides || this.totalSlides <= 1) {
        return 100;
      }
      return 100 / this.totalSlides;
    }

    /**
     * Calculate maximum offset percentage.
     * @returns {number}
     */
    getMaxHandleOffsetPercentage() {
      return Math.max(0, 100 - this.getHandleWidthPercentage());
    }

    /**
     * Calculate current thumb offset percentage.
     * @returns {number}
     */
    getThumbOffsetPercentage() {
      if (this.isDragging && this.dragOffsetPercentage !== null) {
        return this.dragOffsetPercentage;
      }

      if (!this.totalSlides || this.totalSlides <= 1) {
        return 0;
      }

      if (this.totalSlides === 2) {
        return this.currentSlideIndex * this.getMaxHandleOffsetPercentage();
      }

      const denominator = this.totalSlides - 1;
      if (denominator <= 0) {
        return 0;
      }

      return (this.currentSlideIndex / denominator) * this.getMaxHandleOffsetPercentage();
    }

    /**
     * Update progress bar position and ARIA attributes.
     */
    updateProgressBar() {
      if (this.splide && this.totalSlides > 1) {
        const currentIndex = this.splide.index;
        this.currentSlideIndex = currentIndex;

        if (!this.isDragging) {
          this.dragOffsetPercentage = null;
        }

        const handleWidth = this.getHandleWidthPercentage();
        const offset = this.getThumbOffsetPercentage();

        this.thumb.style.width = handleWidth + '%';
        this.thumb.style.left = offset + '%';
        this.thumb.setAttribute('aria-valuenow', currentIndex + 1);
        this.thumb.setAttribute(
          'aria-label',
          'Slide ' + (currentIndex + 1) + ' of ' + this.totalSlides
        );
      }
    }

    /**
     * Handle click on progress track.
     * @param {MouseEvent} event
     */
    handleTrackClick(event) {
      if (
        !this.splide ||
        !this.totalSlides ||
        this.totalSlides <= 1 ||
        this.isDragging
      ) {
        return;
      }

      if (event.target.closest('.image-text-usp__progress-thumb')) {
        return;
      }

      const rect = this.track.getBoundingClientRect();
      if (!rect.width) {
        return;
      }

      const relativePosition = (event.clientX - rect.left) / rect.width;
      const clampedRelative = Math.min(Math.max(relativePosition, 0), 1);
      const offset = clampedRelative * this.getMaxHandleOffsetPercentage();

      const targetIndex = this.getIndexFromOffset(offset);
      this.goToSlide(targetIndex);
    }

    /**
     * Handle wheel scroll on carousel.
     * @param {WheelEvent} event
     */
    handleWheelScroll(event) {
      if (
        !this.totalSlides ||
        this.totalSlides <= 1 ||
        this.isDragging
      ) {
        return;
      }

      const deltaX = event.deltaX;
      const deltaY = event.deltaY;
      const shiftKey = event.shiftKey;

      let horizontalDelta = deltaX;

      if (shiftKey) {
        if (!horizontalDelta || Math.abs(deltaY) > Math.abs(horizontalDelta)) {
          horizontalDelta = deltaY;
        }
      }

      if (!horizontalDelta) {
        return;
      }

      if (!shiftKey && Math.abs(horizontalDelta) < Math.abs(deltaY)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const direction = horizontalDelta > 0 ? 1 : -1;
      this.goToSlide(this.currentSlideIndex + direction);
    }

    /**
     * Convert offset percentage to slide index.
     * @param {number} offsetPercentage
     * @returns {number}
     */
    getIndexFromOffset(offsetPercentage) {
      if (!this.totalSlides || this.totalSlides <= 1) {
        return 0;
      }

      const maxOffset = this.getMaxHandleOffsetPercentage();
      if (maxOffset <= 0) {
        return 0;
      }

      const clampedOffset = Math.min(
        Math.max(offsetPercentage, 0),
        maxOffset
      );

      const ratio = clampedOffset / maxOffset;
      const index = Math.round(ratio * (this.totalSlides - 1));
      return Math.min(Math.max(index, 0), this.totalSlides - 1);
    }

    /**
     * Navigate to specific slide.
     * @param {number} targetIndex
     */
    goToSlide(targetIndex) {
      if (
        !this.splide ||
        typeof targetIndex !== 'number' ||
        !Number.isFinite(targetIndex)
      ) {
        return;
      }

      const clampedIndex = Math.min(
        Math.max(targetIndex, 0),
        this.totalSlides - 1
      );

      if (clampedIndex === this.currentSlideIndex) {
        return;
      }

      this.currentSlideIndex = clampedIndex;
      if (!this.isDragging) {
        this.dragOffsetPercentage = null;
      }

      this.splide.go(clampedIndex);
    }

    /**
     * Start dragging progress thumb.
     * @param {PointerEvent} event
     */
    startHandleDrag(event) {
      if (
        !this.splide ||
        !this.totalSlides ||
        this.totalSlides <= 1
      ) {
        return;
      }

      event.preventDefault();
      this.isDragging = true;
      this.dragPointerId = event.pointerId;
      this.dragInitialClientX = event.clientX;
      this.dragInitialOffset = this.getThumbOffsetPercentage();
      this.dragOffsetPercentage = this.getThumbOffsetPercentage();

      this.thumb.classList.add('is-dragging');

      if (this.thumb.setPointerCapture) {
        this.thumb.setPointerCapture(event.pointerId);
      }

      window.addEventListener('pointermove', this.handleThumbDragMove);
      window.addEventListener('pointerup', this.stopHandleDrag);
      window.addEventListener('pointercancel', this.stopHandleDrag);
    }

    /**
     * Update thumb position while dragging.
     * @param {PointerEvent} event
     */
    handleThumbDragMove(event) {
      if (!this.isDragging || event.pointerId !== this.dragPointerId) {
        return;
      }

      const rect = this.track.getBoundingClientRect();
      if (!rect.width) {
        return;
      }

      const deltaX = event.clientX - this.dragInitialClientX;
      const deltaPercentage = (deltaX / rect.width) * 100;
      const nextOffset = Math.min(
        Math.max(this.dragInitialOffset + deltaPercentage, 0),
        this.getMaxHandleOffsetPercentage()
      );

      this.dragOffsetPercentage = nextOffset;

      const handleWidth = this.getHandleWidthPercentage();
      this.thumb.style.width = handleWidth + '%';
      this.thumb.style.left = nextOffset + '%';

      const targetIndex = this.getIndexFromOffset(nextOffset);
      this.goToSlide(targetIndex);
    }

    /**
     * Stop dragging progress thumb.
     * @param {PointerEvent} event
     */
    stopHandleDrag(event) {
      if (!this.isDragging) {
        return;
      }

      if (event && event.pointerId && this.thumb.releasePointerCapture) {
        try {
          this.thumb.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Ignore release errors
        }
      }

      this.isDragging = false;
      this.dragPointerId = null;
      this.dragInitialClientX = 0;
      this.dragInitialOffset = 0;
      this.dragOffsetPercentage = null;

      this.thumb.classList.remove('is-dragging');

      window.removeEventListener('pointermove', this.handleThumbDragMove);
      window.removeEventListener('pointerup', this.stopHandleDrag);
      window.removeEventListener('pointercancel', this.stopHandleDrag);
    }

    /**
     * Handle keyboard navigation on thumb.
     * @param {KeyboardEvent} event
     */
    handleThumbKeydown(event) {
      if (!this.totalSlides || this.totalSlides <= 1) {
        return;
      }

      let handled = false;
      let targetIndex = this.currentSlideIndex;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          targetIndex = this.currentSlideIndex - 1;
          handled = true;
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          targetIndex = this.currentSlideIndex + 1;
          handled = true;
          break;
        case 'Home':
          targetIndex = 0;
          handled = true;
          break;
        case 'End':
          targetIndex = this.totalSlides - 1;
          handled = true;
          break;
        default:
          break;
      }

      if (handled) {
        event.preventDefault();
        this.goToSlide(targetIndex);
      }
    }
  }
})();

